import { ActionIcon, Box, Flex, Icon, Loader, Stack, TFNotifier, TFText, Tooltip } from '@tf/ui';
import { fmt } from '@tf/utils';
import React from 'react';

import { isPreviewableFile } from '@/core/api/files';
import { downloadAttachedFile, getAttachedFileBlob, useFilesInfo } from '@/core/api/payments';

interface Props {
	supportingDocuments?: { _object: string }[];
}

const SupportingDocumentPreviewButton: React.FC<{ fileId: string; fileName: string }> = ({ fileId, fileName }) => {
	const handlePreview = React.useCallback(async () => {
		try {
			const blob = await getAttachedFileBlob(fileId);
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			URL.revokeObjectURL(url);
		} catch (e) {
			console.error('Preview failed:', e);
			TFNotifier.error('File preview failed', {
				id: 'SupportingDocumentPreviewError',
			});
		}
	}, [fileId]);

	return (
		<Tooltip label={`Preview ${fileName}`} withArrow>
			<ActionIcon variant="transparent" color="blue">
				<Icon.IconEye size={22} stroke={1.5} onClick={handlePreview} />
			</ActionIcon>
		</Tooltip>
	);
};

export const SupportingDocuments: React.FC<Props> = ({ supportingDocuments }) => {
	const supportingDocumentsQueryResult = useFilesInfo(supportingDocuments?.map((a) => a._object) ?? []);

	return (
		<>
			{Boolean(supportingDocuments?.length) && (
				<Box mt="32px">
					<TFText fz="16px" lh="18px" fw="600">
						Supporting documents
					</TFText>
					{supportingDocumentsQueryResult.map(({ data, isPending }, idx) => {
						const canPreview = data ? isPreviewableFile(data.meta['@mime'] || '') : false;

						return (
							<Flex
								sx={{ borderRadius: '8px', minHeight: '59px', width: '100%' }}
								mt="12px"
								p="12px"
								bg="#F8FAFC"
								key={idx}
							>
								<Flex w="100%" justify="space-between" align="center">
									<Flex align="center">
										<Icon.IconFileText color="#6B7280" size={24} />
										{isPending ? (
											<Loader type="oval" ml="8px" size="20" />
										) : (
											<Stack ml="8px" gap="2px">
												<TFText lh="normal" fw="500" fz="14px">
													{data?.meta['@filename']}
												</TFText>
												<TFText lh="normal" fz="13px" c="#64748B">
													{fmt.toDate(data?.meta['@createdAt'], {
														preset: 'full_date_with_time',
													})}
												</TFText>
											</Stack>
										)}
									</Flex>
									<Box>
										{canPreview && data && (
											<SupportingDocumentPreviewButton fileId={data.id} fileName={data.meta['@filename'] || 'file'} />
										)}
										<ActionIcon variant="transparent" color="#2563EB">
											<Icon.IconDownload
												size={22}
												stroke={1.5}
												onClick={async () => {
													if (!data) return;
													await downloadAttachedFile(data.id, data?.meta['@filename']);
												}}
											/>
										</ActionIcon>
									</Box>
								</Flex>
							</Flex>
						);
					})}
				</Box>
			)}
		</>
	);
};
